import React, { useEffect, useState, useCallback } from 'react';
import mondaySdk from 'monday-sdk-js';
import { FileSignature, Files, Clock } from 'lucide-react';
import { Connect, Settings } from '@vibe/icons';
import EnvelopeBuilder from './EnvelopeBuilder';
import TemplatesTab from './TemplatesTab';
import DocumentsTab from './DocumentsTab';
import TemplateEditor from './editor/TemplateEditor';
import SettingsTab from './settings/SettingsTab';
import * as SignService from '../service/sign';

const monday = mondaySdk();
monday.setApiVersion('2026-01');

/**
 * Left to right in the order a first-time user meets them, and joined by
 * chevrons so the header reads as a route rather than three unrelated places:
 * build a request, set up how the board sends, then watch it come back.
 *
 * Settings used to sit last, behind a divider, deliberately demoted as
 * configuration rather than a third thing you do per item. It moved because
 * that reading is only correct once the board works — on a fresh board it hid
 * the single setting standing between someone and their first send.
 *
 * The connector is a dashed hairline rather than a chevron, and unnumbered.
 * The builder's own wizard is a numbered, chevron-separated sequence sitting
 * directly below this one — reusing either glyph here merges the two rows into
 * one apparent list. See the separator itself for the full reasoning.
 */
const TABS = [
  { id: 'new', label: 'New request', icon: FileSignature },
  { id: 'track', label: 'Track signatures', icon: Files },
];

/**
 * Settings sits apart from those two, after a divider, because it is not a peer
 * of them. Those are things you DO to this item, over and over; this is how the
 * board is configured, visited once and then rarely.
 *
 * It briefly moved into the middle of the row, joined by connectors, to spell
 * out the order a first-time sender meets them. That order is true exactly
 * once — on the second request Settings is a stop nobody makes — and the
 * connectors collided with the builder's own numbered stepper sitting directly
 * below. The divider says "configuration" without claiming a sequence.
 */
const SETTINGS_TAB = { id: 'settings', label: 'Settings' };
const SettingsIcon = Settings;

/**
 * SmartSign item view shell.
 *
 * Two tabs: build a signing request, and track the ones already sent. Tab 1
 * opens the builder DIRECTLY rather than behind a launcher — the request is the
 * app's front door.
 *
 * An item carries ONE request at a time, so tab 1 has no list and no navigation:
 * it resolves the item's slot on mount and either reopens the half-built request
 * where you left it, blocks with `RequestInFlight` while one is out for
 * signature, or gives you a clean builder. Nothing to find, nothing to pick.
 *
 * Templates are a means to a request, not a peer of it, so they are not in this
 * header at all: step 1 lists them where a starting point is actually being
 * chosen, and routes back here for the list and the editor.
 */
export default function SmartSignApp() {
  const [context, setContext] = useState(null);
  const [activeTab, setActiveTab] = useState('new');
  // 'builder' | 'templates' | { editTemplate: id }
  const [view, setView] = useState('builder');
  const [builderKey, setBuilderKey] = useState(0);
  const [board, setBoard] = useState(null);
  const [templates, setTemplates] = useState([]);
  // undefined = not asked yet; then { draft, inFlight } — see getOpenRequest.
  const [open, setOpen] = useState(undefined);

  useEffect(() => {
    const unsubscribe = monday.listen('context', (res) => setContext(res.data || {}));
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  const getSessionToken = useCallback(async () => {
    const res = await monday.get('sessionToken');
    return res.data;
  }, []);

  const boardId = context?.boardId ?? context?.boardIds?.[0] ?? null;
  const itemId = context?.itemId ?? null;

  const loadBoard = useCallback(async () => {
    if (!boardId) return;
    try {
      const token = await getSessionToken();
      const res = await SignService.getBoardColumns(token, boardId);
      if (res?.ok) setBoard(res);
    } catch (err) {
      console.error('Failed to load board columns', err);
    }
  }, [boardId, getSessionToken]);

  const loadTemplates = useCallback(async () => {
    if (!boardId) return;
    try {
      const token = await getSessionToken();
      const res = await SignService.listTemplates(token, boardId);
      // ALL of them: step 1's picker shows published ones as selectable and
      // uses the rest to explain why they are not.
      if (res?.ok) setTemplates(res.templates || []);
    } catch (err) {
      console.error('Failed to load templates', err);
    }
  }, [boardId, getSessionToken]);

  /**
   * Resolve the item's single request slot.
   *
   * Called on mount and after anything that can change it — a send, a discard,
   * a cancel — because the answer decides what tab 1 is even allowed to show.
   */
  const loadOpen = useCallback(async () => {
    if (!itemId) return;
    try {
      const token = await getSessionToken();
      const res = await SignService.getOpenRequest(token, itemId);
      setOpen(res?.ok ? { draft: res.draft, inFlight: res.inFlight } : { draft: null, inFlight: null });
    } catch (err) {
      console.error('Failed to resolve the item request', err);
      setOpen({ draft: null, inFlight: null });
    }
  }, [itemId, getSessionToken]);

  useEffect(() => {
    loadBoard();
    loadTemplates();
    loadOpen();
  }, [loadBoard, loadTemplates, loadOpen]);

  /**
   * Create a template and open it.
   *
   * Owned here rather than in the templates list because step 1 needs it too —
   * a board with no templates offers "Create a template" straight from the
   * starters section, and both doors should land in the same place.
   */
  const createTemplate = useCallback(async () => {
    if (!boardId) return;
    try {
      const token = await getSessionToken();
      const res = await SignService.createTemplate(token, boardId, 'Untitled template');
      if (res?.ok && res.template) {
        setView({ editTemplate: res.template.id });
        loadTemplates();
      } else {
        monday.execute('notice', { message: 'Could not create the template.', type: 'error' });
      }
    } catch (err) {
      console.error('Failed to create sign template', err);
      monday.execute('notice', { message: 'Could not create the template.', type: 'error' });
    }
  }, [boardId, getSessionToken, loadTemplates]);

  if (!context) {
    return (
      <div className='flex items-center justify-center h-screen text-sm text-gray-500'>Loading…</div>
    );
  }

  /**
   * Back to tab 1, re-resolving the item's slot.
   *
   * The remount is what makes the builder pick up the resolved draft id — or
   * start clean once a draft has been discarded or sent.
   */
  const resetBuilder = () => {
    setView('builder');
    setBuilderKey((k) => k + 1);
    // Back to "not asked yet" FIRST. Without this the builder remounts against
    // the previous answer — after a discard that is a deleted id, so it would
    // mount, 404, and stay in its error state even once the real answer lands.
    setOpen(undefined);
    loadOpen();
    loadTemplates();
  };

  const editingTemplateId = typeof view === 'object' ? view.editTemplate : null;
  /** A wizard is on screen — the builder or the template editor, tab 1 only. */
  const isWizard = activeTab === 'new' && (editingTemplateId !== null || view === 'builder');

  return (
    <div className='flex flex-col h-screen overflow-hidden bg-gray-50'>
      {/*
        Three columns, not two: the tabs are the middle one, and the flex-1
        spacer on the left mirrors the brand block's column on the right so the
        nav is centred against the HEADER rather than against whatever is left
        over next to the brand.
      */}
      <header className='flex-shrink-0 flex items-center gap-3 px-4 py-2.5 bg-white border-b border-gray-200'>
        <span className='flex-1' />
        <nav className='flex flex-shrink-0 items-center gap-1'>
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  // Leaving the builder is safe — everything autosaves, and
                  // coming back re-resolves the item's slot and reopens it.
                  if (tab.id === 'new') resetBuilder();
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  isActive ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}

          <span className='w-px h-5 mx-1.5 bg-gray-200' aria-hidden='true' />

          <button
            onClick={() => setActiveTab(SETTINGS_TAB.id)}
            title='How this board sends signature requests'
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
              activeTab === SETTINGS_TAB.id
                ? 'bg-blue-50 text-blue-700 font-medium'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <SettingsIcon size={15} />
            {SETTINGS_TAB.label}
          </button>
        </nav>

        <div className='flex-1 min-w-0 flex justify-end'>
          <div className='min-w-0 text-right'>
            <div className='text-sm font-semibold text-gray-900 leading-tight'>SmartSign</div>
            <div className='text-xs text-gray-500 leading-tight'>eSignatures on monday (v1.0)</div>
          </div>
        </div>
      </header>

      {/*
        Two layouts: the wizards own their own scrolling (a fixed header, a
        scrolling step body, a fixed footer), everything else is a plain
        scrolling page.

        Keyed on what is actually RENDERED, not on `view` alone — `view` stays
        'builder' while you are on the track tab, which used to hand the tracking
        list the wizard's layout. That made it lay out unlike the otherwise
        identical templates list, and trapped it behind `overflow-hidden` once it
        grew past the viewport.
      */}
      <main
        className={`flex-1 ${
          isWizard ? 'overflow-hidden flex flex-col' : 'overflow-y-auto'
        }`}
      >
        {activeTab === 'settings' ? (
          <SettingsTab
            monday={monday}
            getSessionToken={getSessionToken}
            boardId={boardId}
            board={board}
            onBoardChanged={loadBoard}
          />
        ) : activeTab === 'track' ? (
          <DocumentsTab
            monday={monday}
            getSessionToken={getSessionToken}
            itemId={itemId}
            onStartRequest={() => {
              setActiveTab('new');
              resetBuilder();
            }}
          />
        ) : editingTemplateId ? (
          <TemplateEditor
            monday={monday}
            getSessionToken={getSessionToken}
            templateId={editingTemplateId}
            boardId={boardId}
            itemId={itemId}
            board={board}
            onClose={() => {
              setView('templates');
              loadTemplates();
            }}
            onPublished={() => {
              // Straight to the builder, not back to the template list: the
              // template is finished, and the next thing anyone does with a
              // finished template is send it.
              setActiveTab('new');
              setView('builder');
              loadTemplates();
            }}
          />
        ) : view === 'templates' ? (
          <TemplatesTab
            monday={monday}
            getSessionToken={getSessionToken}
            boardId={boardId}
            onEdit={(templateId) => setView({ editTemplate: templateId })}
            onCreate={createTemplate}
            onBack={resetBuilder}
          />
        ) : open === undefined ? (
          <div className='py-16 text-center text-sm text-gray-500'>Loading…</div>
        ) : open.inFlight ? (
          <RequestInFlight
            request={open.inFlight}
            onTrack={() => setActiveTab('track')}
          />
        ) : (
          <EnvelopeBuilder
            // The resolved id is part of the key: if the slot changes underneath
            // (discarded, or sent from another tab) the builder starts over
            // rather than holding a stale envelope.
            key={`${builderKey}-${open.draft?.id ?? 'new'}`}
            monday={monday}
            getSessionToken={getSessionToken}
            boardId={boardId}
            itemId={itemId}
            envelopeId={open.draft?.id ?? null}
            board={board}
            templates={templates}
            onBoardChanged={loadBoard}
            onSent={() => {
              setActiveTab('track');
              resetBuilder();
            }}
            /* The send gate blocks on a board with no file column, and the
               request panel is where that surfaces — so it needs a way to get
               here rather than just naming the problem. */
            onOpenSettings={() => setActiveTab('settings')}
            onDiscarded={resetBuilder}
            onManageTemplates={() => setView('templates')}
            onEditTemplate={(templateId) => setView({ editTemplate: templateId })}
            onCreateTemplate={createTemplate}
          />
        )}
      </main>
    </div>
  );
}

/**
 * Tab 1 while a request is already out for signature.
 *
 * An item carries one request at a time, so there is nothing to build here yet.
 * The blocker states the two ways forward rather than just refusing — waiting
 * for the signers, or cancelling in the tracking tab.
 */
function RequestInFlight({ request, onTrack }) {
  return (
    <div className='max-w-2xl mx-auto px-4 py-5'>
      <div className='bg-white border border-gray-200 rounded-lg px-6 py-10 text-center'>
        <Clock size={26} className='mx-auto text-blue-600 mb-3' />
        <div className='text-sm font-semibold text-gray-900'>
          A request is out for signature
        </div>
        <p className='text-sm text-gray-500 mt-1.5 max-w-sm mx-auto'>
          &ldquo;{request.name}&rdquo; — {request.documentCount}{' '}
          {request.documentCount === 1 ? 'document' : 'documents'} with {request.signerCount}{' '}
          {request.signerCount === 1 ? 'signer' : 'signers'}. This item takes one request at a time,
          so the next one starts once this is signed or cancelled.
        </p>
        <button
          onClick={onTrack}
          className='mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700'
        >
          <Connect size={14} />
          Track it
        </button>
      </div>
    </div>
  );
}
